
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { saveAs } from 'file-saver';
import "react-datepicker/dist/react-datepicker.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import { getAdjustedStartDate, getAdjustedEndDate } from '../../utils/dateHelper';
import LedgerDetailsOffcanvas from './LedgerDetailsOffcanvas';

const ProfitAndLossStatement = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dateRangeFilter, setDateRangeFilter] = useState('yearly');
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedEndYear, setSelectedEndYear] = useState(null);
    const [showDrilldown, setShowDrilldown] = useState(false);
    const [selectedLedger, setSelectedLedger] = useState(null);

    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');
    const Api = process.env.REACT_APP_WEBZASH_URL;
    const apiKey = process.env.REACT_APP_API_KEY;
    const APIurl = process.env.REACT_APP_BACKEND_URL;
    const getDayjsDate = (dateString) => {
        return dateString ? dayjs(dateString) : null;
    };

    const handleDateRangeChange = (value) => {
        setDateRangeFilter(value);
        if (value === 'yearly') {
            setSelectedYear(null);
            setSelectedEndYear(null);
            setStartDate('');
            setEndDate('');
        }
    };

    useEffect(() => {
        fetchProjects();
        fetchProfitLossData();
    }, []);

    const fetchProjects = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const response = await axios.get(`${APIurl}/api/projects`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });
            setProjects(response.data?.Data);
        } catch (error) {
            console.error('Error fetching projects:', error);
        }
    };

    const fetchProfitLossData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (startDate) params.startdate = startDate;
            if (endDate) params.enddate = endDate;
            if (selectedProject) params.project_id = selectedProject;

            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${APIurl}/api/profit-and-loss`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                    'Accept': 'application/json'
                },
                params,
                skipLoader: true
            });

            const processedData = processApiData(response.data.data);
            setData(processedData);

        } catch (error) {
            console.error('Error fetching profit/loss data:', error);
        } finally {
            setLoading(false);
        }
    };

    const processApiData = (apiData) => {
        if (!apiData) return null;

        const processItems = (items) => {
            return items?.map(item => ({
                ...item,
                amount: parseFloat(item.amount) || 0,
                is_group: item.children && item.children.length > 0,
                children: item.children ? processItems(item.children) : []
            }));
        };
        return {
            ...apiData,
            gross_income_total: parseFloat(apiData.gross_income_total) || 0,
            gross_expense_total: parseFloat(apiData.gross_expense_total) || 0,
            gross_pl: parseFloat(apiData.gross_pl) || 0,
            net_income_total: parseFloat(apiData.net_income_total) || 0,
            net_expense_total: parseFloat(apiData.net_expense_total) || 0,
            net_pl: parseFloat(apiData.net_pl) || 0,
            gross_incomes: {
                ...apiData.gross_incomes,
                children: processItems(apiData.gross_incomes?.children || [])
            },
            gross_expenses: {
                ...apiData.gross_expenses,
                children: processItems(apiData.gross_expenses?.children || [])
            },
            net_incomes: {
                ...apiData.net_incomes,
                children: processItems(apiData.net_incomes?.children || [])
            },
            net_expenses: {
                ...apiData.net_expenses,
                children: processItems(apiData.net_expenses?.children || [])
            }
        };
    };

    const handleExport = async (format) => {
        try {
            if (format === 'pdf') {
                // Call local backend for PDF export
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                const params = {
                    startdate: startDate,
                    enddate: endDate
                };
                if (selectedProject) params.project_id = selectedProject;

                const response = await axios.get(`${APIurl}/api/profitloss/export-pdf`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Tenant-Origin': origin,
                        'Accept': 'application/pdf'
                    },
                    params,
                    responseType: 'blob'
                });

                const blob = new Blob([response.data], { type: 'application/pdf' });
                saveAs(blob, `ProfitAndLoss_${startDate || 'All'}_to_${endDate || 'All'}.pdf`);
            } else {
                // CSV/Excel export via ERP backend
                const params = new URLSearchParams();
                if (startDate) params.append('startdate', startDate);
                if (endDate) params.append('enddate', endDate);
                if (selectedProject !== '') params.append('project_id', selectedProject);
                params.append('format', format);

                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                const response = await axios.get(`${APIurl}/api/profit-and-loss?${params.toString()}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Tenant-Origin': origin,
                        'Accept': 'application/octet-stream'
                    },
                    responseType: 'blob',
                    skipLoader: true
                });

                const contentDisposition = response.headers['content-disposition'];
                let filename = `profitloss.${format}`;
                if (contentDisposition && contentDisposition.includes('filename=')) {
                    filename = contentDisposition
                        .split('filename=')[1]
                        .replace(/"/g, '')
                        .trim();
                }

                const blob = new Blob([response.data], {
                    type: format === 'csv' ? 'text/csv' : 'application/vnd.ms-excel'
                });

                saveAs(blob, filename);
            }
        } catch (error) {
            console.error(`Error exporting report in ${format} format:`, error);
        }
    };
    const handleClearFilters = () => {
        setStartDate('');
        setEndDate('');
        setSelectedYear(null);
        setSelectedEndYear(null);
        setDateRangeFilter('yearly');
        setSelectedProject('');
        fetchProfitLossData();
    };


    const renderAccountItem = (item, index, type) => {
        const isGroup = item.children && item.children.length > 0;
        const showDrAmount = item.cl_total_dc === 'D' && parseFloat(item.amount) > 0;
        const showCrAmount = item.cl_total_dc === 'C' && parseFloat(item.amount) > 0;

        return (
            <div
                key={index}
                className={`account-item level-${item.level} ${isGroup ? 'group-row' : 'ledger-row cursor-pointer clickable-item'}`}
                onClick={() => {
                    if (!isGroup) {
                        setSelectedLedger({ ...item, type, is_group: isGroup });
                        setShowDrilldown(true);
                    }
                }}
            >
                <div className="account-name d-flex align-items-center">
                    {isGroup ? (
                        <i className="fas fa-chevron-down text-muted me-2" style={{ fontSize: '0.7rem' }}></i>
                    ) : (
                        <i className="fas fa-square text-primary me-2" style={{ fontSize: '0.35rem', opacity: 0.5, marginLeft: '4px' }}></i>
                    )}
                    <span className={isGroup ? 'fw-bold text-dark' : 'text-secondary'}>{item.name}</span>
                    <i className="fas fa-search-plus ms-2 drilldown-icon" title="Click to view details" style={{ fontSize: '0.6rem' }}></i>
                </div>
                <div className={`account-amount ${isGroup ? 'fw-bold text-dark' : 'text-dark'}`}>
                    {showDrAmount && `Dr ${Math.abs(item.amount).toLocaleString('en-AE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })}`}
                    {showCrAmount && `Cr ${Math.abs(item.amount).toLocaleString('en-AE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })}`}
                    {!showDrAmount && !showCrAmount && '0.00'}
                </div>
            </div>
        );
    };

    const renderAccountGroup = (items, type) => {
        if (!Array.isArray(items)) return null;
        return items.map((item, index) => {
            const isGroup = item.children && item.children.length > 0;
            return (
                <div key={index} className="account-row-wrapper">
                    {renderAccountItem(item, index, type)}
                    {isGroup && (
                        <div className="account-children ps-3 border-start ms-2 mb-2">
                            {renderAccountGroup(item.children, type)}
                        </div>
                    )}
                </div>
            );
        });
    };


    const renderTwoColumnSection = (
        leftTitle,
        leftItems,
        leftLabel1,
        leftAmount1,
        leftLabel2,
        leftAmount2,
        leftTotal,
        leftDc,

        rightTitle,
        rightItems,
        rightLabel1,
        rightAmount1,
        rightLabel2,
        rightAmount2,
        rightTotal,
        rightDc
    ) => {
        const formatCurrency = (amount, dc = 'D') => {
            const absAmount = Math.abs(amount);
            const formatted = new Intl.NumberFormat('en-AE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(absAmount);

            return amount >= 0 ?
                (dc === 'D' ? `Dr ${formatted}` : `Cr ${formatted}`) :
                (dc === 'D' ? `Cr ${formatted}` : `Dr ${formatted}`);
        };

        return (
            <div className="two-column-section">
                <div className="column debit-side">
                    <h4 className="section-title">{leftTitle}</h4>
                    <div className="account-group">{renderAccountGroup(leftItems, 'expense')}</div>

                    {leftLabel1 && (
                        <div className="extra-item">
                            <div className="account-name">{leftLabel1}</div>
                            <div className="account-amount">{formatCurrency(leftAmount1, leftDc)}</div>
                        </div>
                    )}

                    {leftLabel2 && (
                        <div className="extra-item">
                            <div className="account-name">{leftLabel2}</div>
                            <div className="account-amount">{formatCurrency(leftAmount2, leftDc)}</div>
                        </div>
                    )}

                    <div className="section-total">
                        <strong>Total</strong>
                        <strong className="total-amount">{formatCurrency(leftTotal, leftDc)}</strong>
                    </div>
                </div>

                <div className="column credit-side">
                    <h4 className="section-title">{rightTitle}</h4>
                    <div className="account-group">{renderAccountGroup(rightItems, 'income')}</div>

                    {rightLabel1 && (
                        <div className="extra-item">
                            <div className="account-name">{rightLabel1}</div>
                            <div className="account-amount">{formatCurrency(rightAmount1, rightDc)}</div>
                        </div>
                    )}

                    {rightLabel2 && (
                        <div className="extra-item">
                            <div className="account-name">{rightLabel2}</div>
                            <div className="account-amount">{formatCurrency(rightAmount2, rightDc)}</div>
                        </div>
                    )}

                    <div className="section-total">
                        <strong>Total</strong>
                        <strong className="total-amount">{formatCurrency(rightTotal, rightDc)}</strong>
                    </div>
                </div>
            </div>
        );
    };


    const renderProfitSection = (title, amount, isTotal = false) => {
        const isProfit = amount >= 0;
        return (
            <div className={`profit-section ${isProfit ? 'profit' : 'loss'} ${isTotal ? 'total' : ''}`}>
                <h5>{title}</h5>
                <div className="profit-amount">
                    {isProfit ? `Cr ${Math.abs(amount).toLocaleString('en-AE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })}` : `Dr ${Math.abs(amount).toLocaleString('en-AE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })}`}
                    <span className="profit-label">
                        ({isProfit ? 'Profit' : 'Loss'})
                    </span>
                </div>
            </div>
        );
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    {/* Header */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="page-title-box d-flex align-items-center justify-content-between">
                                <div>
                                    <h4 className="mb-0 text-primary">Profit and Loss Statement</h4>
                                    <p className="text-muted mb-0 small">
                                        Summary of revenues, costs, and expenses
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        onClick={() => handleExport('pdf')}
                                        className="btn btn-outline-danger btn-sm"
                                        disabled={loading || !data}
                                    >
                                        <i className="fas fa-file-pdf me-1"></i> PDF
                                    </button>
                                  
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Unified Filters */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card shadow-sm border-0">
                                <div className="card-body p-3">
                                    <div className="row g-3 align-items-end">
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small mb-1">Date Range</label>
                                            <select
                                                className="form-select form-select-sm"
                                                value={dateRangeFilter}
                                                onChange={(e) => handleDateRangeChange(e.target.value)}
                                            >
                                                <option value="yearly">Yearly</option>
                                                <option value="custom">Custom Range</option>
                                            </select>
                                        </div>

                                        {dateRangeFilter === 'yearly' && (
                                            <>
                                                <div className="col-lg-2 col-md-3">
                                                    <label className="form-label font-size-12 text-uppercase fw-bold text-muted mb-1">Start Date</label>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker
                                                            views={["year", "month", "day"]}
                                                            openTo="year"
                                                            format="DD-MM-YYYY"
                                                            value={startDate ? dayjs(startDate) : null}
                                                            onChange={(newValue) => {
                                                                if (newValue) {
                                                                    setSelectedYear(newValue);
                                                                    if (!startDate || dayjs(startDate).year() !== newValue.year()) {
                                                                        const start = newValue.startOf('year');
                                                                        setStartDate(start.format('YYYY-MM-DD'));
                                                                        setEndDate(newValue.endOf('year').format('YYYY-MM-DD'));
                                                                        setSelectedEndYear(newValue);
                                                                    } else {
                                                                        const { startDate: newStart, suggestedEndDate } = getAdjustedStartDate(newValue, endDate);
                                                                        setStartDate(newStart);
                                                                        if (suggestedEndDate) {
                                                                            setEndDate(suggestedEndDate);
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                                                        />
                                                    </LocalizationProvider>
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label text-muted small mb-1">End Date</label>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker
                                                            views={["year", "month", "day"]}
                                                            openTo="year"
                                                            format="DD-MM-YYYY"
                                                            value={endDate ? dayjs(endDate) : null}
                                                            onChange={(newValue) => {
                                                                if (newValue) {
                                                                    setSelectedEndYear(newValue);
                                                                    if (!endDate || dayjs(endDate).year() !== newValue.year()) {
                                                                        setEndDate(newValue.endOf('year').format('YYYY-MM-DD'));
                                                                    } else {
                                                                        const dateStr = getAdjustedEndDate(newValue, startDate);
                                                                        setEndDate(dateStr);
                                                                    }
                                                                }
                                                            }}
                                                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                                                        />
                                                    </LocalizationProvider>
                                                </div>
                                            </>
                                        )}

                                        {dateRangeFilter === 'custom' && (
                                            <>
                                                <div className="col-md-2">
                                                    <label className="form-label text-muted small mb-1">From Date</label>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker
                                                            views={["year", "month", "day"]}
                                                            format="DD-MM-YYYY"
                                                            value={startDate ? dayjs(startDate) : null}
                                                            onChange={(newValue) => {
                                                                const { startDate: newStart, suggestedEndDate } = getAdjustedStartDate(newValue, endDate);
                                                                setStartDate(newStart);
                                                                if (suggestedEndDate) {
                                                                    setEndDate(suggestedEndDate);
                                                                }
                                                            }}
                                                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                                                        />
                                                    </LocalizationProvider>
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label text-muted small mb-1">To Date</label>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker
                                                            views={["year", "month", "day"]}
                                                            format="DD-MM-YYYY"
                                                            value={endDate ? dayjs(endDate) : null}
                                                            onChange={(newValue) => {
                                                                setEndDate(getAdjustedEndDate(newValue, startDate));
                                                            }}
                                                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                                                        />
                                                    </LocalizationProvider>
                                                </div>
                                            </>
                                        )}

                                        <div className="col-md-3">
                                            <label className="form-label text-muted small mb-1">Project</label>
                                            <select
                                                value={selectedProject}
                                                onChange={(e) => setSelectedProject(e.target.value)}
                                                className="form-select form-select-sm"
                                            >
                                                <option value="">All Projects</option>
                                                {projects.map(project => (
                                                    <option key={project.id} value={project.id}>
                                                        {project.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-3 d-flex gap-2">
                                            <button
                                                className="btn btn-primary btn-sm px-4"
                                                onClick={fetchProfitLossData}
                                                disabled={loading}
                                            >
                                                {loading ? 'Applying...' : 'Apply Filters'}
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary btn-sm px-4"
                                                onClick={handleClearFilters}
                                                disabled={loading}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Report Content */}
                    <div className="row">
                        <div className="col-12">
                            <div className="profit-loss-container">
                                {loading && !data ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3 text-muted">Analyzing financial performance...</p>
                                    </div>
                                ) : data ? (
                                    <div className="report-content">
                                        {/* Gross Section */}
                                        {renderTwoColumnSection(
                                            'Gross Expenses',
                                            data.gross_expenses.children || [],
                                            'Total Gross Expenses',
                                            data.gross_expense_total,
                                            data.gross_pl > 0 ? 'Gross Profit C/D' : null,
                                            data.gross_pl > 0 ? data.gross_pl : null,
                                            data.gross_expense_side_total,
                                            'D',
                                            'Gross Income',
                                            data.gross_incomes.children || [],
                                            'Total Gross Incomes',
                                            data.gross_income_total,
                                            null,
                                            null,
                                            data.gross_income_side_total,
                                            'C',
                                        )}

                                        {/* Gross Profit Row */}
                                        {renderProfitSection('Gross Profit/Loss', data.gross_pl)}

                                        {/* Net Section */}
                                        {renderTwoColumnSection(
                                            'Net Expenses',
                                            data.net_expenses.children || [],
                                            'Total Expenses',
                                            data.net_expense_total,
                                            'Net Profit',
                                            data.net_pl,
                                            data.net_expense_side_total,
                                            'D',
                                            'Net Income',
                                            data.net_incomes.children || [],
                                            'Total Incomes',
                                            data.net_income_total,
                                            data.gross_pl > 0 ? 'Gross Profit B/D' : null,
                                            data.gross_pl > 0 ? data.gross_pl : null,
                                            data.net_income_side_total,
                                            'C',
                                        )}

                                        {/* Net Profit Row */}
                                        {renderProfitSection('Net Profit/Loss', data.net_pl, true)}
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <p className="text-muted">No report data available for the selected period</p>
                                    </div>
                                )}

                                <div className="report-footer text-muted small text-center mt-4">
                                    Report generated on {moment().format('MMM D, YYYY h:mm A')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CSS and Offcanvas */}
            <style jsx>{`
                .profit-loss-container {
                    background: white;
                    border-radius: 8px;
                    padding: 30px;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
                }
                
                .two-column-section {
                    display: flex;
                    gap: 20px;
                    margin-bottom: 25px;
                }
                
                .column {
                    flex: 1;
                    border: 1px solid #e9ecef;
                    border-radius: 8px;
                    padding: 15px;
                    background: #fdfdfd;
                }
                
                .section-title {
                    font-size: 1rem;
                    font-weight: 700;
                    color: #2c3e50;
                    margin-bottom: 12px;
                    padding-bottom: 8px;
                    border-bottom: 2px solid #3498db;
                }
                
                .account-item {
                    display: flex;
                    justify-content: space-between;
                    padding: 5px 0;
                    font-size: 0.9rem;
                    border-bottom: 1px solid #f1f1f1;
                }
                
                .account-name {
                    flex: 1;
                    color: #34495e;
                }
                
                .account-amount {
                    font-weight: 600;
                    color: #2c3e50;
                    min-width: 100px;
                    text-align: right;
                }
                
                .section-total {
                    display: flex;
                    justify-content: space-between;
                    padding: 10px 0;
                    margin-top: 15px;
                    border-top: 2px solid #2c3e50;
                    font-weight: 700;
                    font-size: 0.95rem;
                }
                
                .profit-section {
                    margin: 20px 0;
                    padding: 15px;
                    border-radius: 8px;
                    text-align: center;
                }
                
                .profit-section.profit {
                    background: #e8f5e9;
                    border: 1px solid #c8e6c9;
                }
                
                .profit-section.loss {
                    background: #ffebee;
                    border: 1px solid #ffcdd2;
                }
                
                .profit-section.total {
                    background: #e3f2fd;
                    border: 1px solid #bbdefb;
                    margin: 30px 0;
                }
                
                .profit-amount {
                    font-weight: 700;
                    font-size: 1.1rem;
                    margin-top: 5px;
                }

                .clickable-item {
                    cursor: pointer;
                    transition: background-color 0.2s;
                }
                
                .clickable-item:hover {
                    background-color: #f8f9fa;
                }
            `}</style>

            {showDrilldown && selectedLedger && (
                <LedgerDetailsOffcanvas
                    show={showDrilldown}
                    onHide={() => {
                        setShowDrilldown(false);
                        setSelectedLedger(null);
                    }}
                    ledger={selectedLedger}
                    startDate={startDate}
                    endDate={endDate}
                    projectId={selectedProject}
                />
            )}
        </div>
    );
};

export default ProfitAndLossStatement;
